/**
 * AvatarOnboardingBuilder — generates a complete voice-clone training pack.
 *
 * Three inputs (agency name, town, optional agent name) generate four
 * AI-written sections in parallel:
 *   1) Area research briefing
 *   2) ~10 min of property narration scripts
 *   3) ~10 min of area knowledge script
 *   4) 20 interview questions
 *
 * Each section is its own async job — the user sees cards pop in as they
 * finish, and can regenerate any one independently. Every section ships
 * with Copy / Autocue / QR-code buttons so the agent can read straight
 * off the phone (or push to a teleprompter rig).
 *
 * On tool open, we ping the backend for any prior pack for this
 * agency+town so the agent can pick up where they left off.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  Mic, Sparkles, Loader2, Copy, ClipboardCheck, RefreshCw, X,
  Maximize2, QrCode, Building2, MapPin, User, FileText, BookOpen,
  HelpCircle, ListChecks, ChevronRight, Volume2, Download, History,
  Clock,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/AuthContext";
import { AppHeader } from "./AppHeader";
import "@/AvatarOnboarding.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_META = {
  research: {
    key: "research",
    label: "Area research",
    eyebrow: "Step 1",
    description: "Talking points your agent can speak naturally from.",
    Icon: BookOpen,
    accent: "research",
  },
  property_script: {
    key: "property_script",
    label: "Property narration",
    eyebrow: "Step 2",
    description: "Five fictional listings written in your NarraTour voiceover style.",
    Icon: FileText,
    accent: "property",
  },
  area_script: {
    key: "area_script",
    label: "Area knowledge",
    eyebrow: "Step 3",
    description: "About ten minutes of spoken-style area copy your agent can read aloud.",
    Icon: MapPin,
    accent: "area",
  },
  interview_qs: {
    key: "interview_qs",
    label: "Interview questions",
    eyebrow: "Step 4",
    description: "Twenty questions to capture natural, long-form speech.",
    Icon: HelpCircle,
    accent: "interview",
  },
};
const SECTION_ORDER = ["research", "property_script", "area_script", "interview_qs"];

export default function AvatarOnboardingBuilder({ onBack }) {
  const { user } = useAuth();

  // Inputs
  const [agencyName, setAgencyName] = useState(user?.agency_name || user?.branch_name || "");
  const [townName, setTownName] = useState("");
  const [agentName, setAgentName] = useState("");

  // Pack state
  const [packId, setPackId] = useState(null);
  // Map of sectionKey -> { status, result, jobId, error }
  const [sections, setSections] = useState({});
  const [isStarting, setIsStarting] = useState(false);

  // Active modals
  const [autocueFor, setAutocueFor] = useState(null);  // sectionKey
  const [qrForSlug, setQrForSlug] = useState(null);    // { slug, title, sectionKey }
  const [showHistory, setShowHistory] = useState(false);
  const [recentPacks, setRecentPacks] = useState([]);

  // Poll job ids
  const pollersRef = useRef({});  // jobId -> intervalId
  const stopAllPollers = () => {
    Object.values(pollersRef.current).forEach((id) => clearInterval(id));
    pollersRef.current = {};
  };

  // Re-load latest pack when the agency + town are filled in
  useEffect(() => {
    let cancelled = false;
    if (!agencyName.trim() || !townName.trim()) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/avatar-onboarding/pack/latest/by-agency`, {
          params: { agency_name: agencyName, town_name: townName },
        });
        if (cancelled || !res.data?.id) return;
        // Auto-restore the previous pack so the agent can pick up where they left off
        setPackId(res.data.id);
        const restored = {};
        SECTION_ORDER.forEach((k) => {
          if (res.data.sections?.[k]) {
            restored[k] = { status: "done", result: res.data.sections[k] };
          }
        });
        setSections(restored);
      } catch {
        /* No prior pack — fine, fresh build */
      }
    }, 600);  // light debounce so we don't spam during typing
    return () => { cancelled = true; clearTimeout(t); };
  }, [agencyName, townName]);

  useEffect(() => () => stopAllPollers(), []);

  // Poll one section job until done/error
  const pollJob = (sectionKey, jobId) => {
    // Clear any existing poller for this job
    if (pollersRef.current[jobId]) clearInterval(pollersRef.current[jobId]);
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/avatar-onboarding/job/${jobId}`);
        const job = res.data;
        if (job.status === "done") {
          clearInterval(id);
          delete pollersRef.current[jobId];
          setSections((prev) => ({
            ...prev,
            [sectionKey]: { status: "done", result: job.result, jobId },
          }));
        } else if (job.status === "error") {
          clearInterval(id);
          delete pollersRef.current[jobId];
          setSections((prev) => ({
            ...prev,
            [sectionKey]: { status: "error", error: job.error, jobId },
          }));
          toast.error(`${SECTION_META[sectionKey].label} failed: ${job.error}`);
        }
      } catch {
        /* keep polling */
      }
    }, 2500);
    pollersRef.current[jobId] = id;
  };

  const handleGeneratePack = async () => {
    if (!agencyName.trim() || !townName.trim()) {
      toast.error("Please fill in your agency name and town.");
      return;
    }
    setIsStarting(true);
    stopAllPollers();
    setSections(() => Object.fromEntries(
      SECTION_ORDER.map((k) => [k, { status: "queued" }])
    ));
    try {
      const res = await axios.post(`${API}/avatar-onboarding/pack/start`, {
        agency_name: agencyName,
        town_name: townName,
        agent_name: agentName,
      });
      setPackId(res.data.pack_id);
      SECTION_ORDER.forEach((k) => {
        const jobId = res.data.job_ids[k];
        setSections((prev) => ({ ...prev, [k]: { status: "running", jobId } }));
        pollJob(k, jobId);
      });
      toast.success("Generating your pack — sections will appear as they're ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start pack generation.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleRegenerate = async (sectionKey) => {
    if (!packId) return;
    setSections((prev) => ({ ...prev, [sectionKey]: { ...prev[sectionKey], status: "running" } }));
    try {
      const res = await axios.post(`${API}/avatar-onboarding/section/regenerate`, {
        pack_id: packId,
        section: sectionKey,
      });
      pollJob(sectionKey, res.data.job_id);
      toast.success(`Regenerating ${SECTION_META[sectionKey].label.toLowerCase()}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate.");
    }
  };

  const handleCopy = (sectionKey) => {
    const text = sections[sectionKey]?.result?.text;
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${SECTION_META[sectionKey].label} copied to clipboard`))
      .catch(() => toast.error("Could not copy — please select and copy manually."));
  };

  // Create an autocue session + open QR modal
  const handleQr = async (sectionKey) => {
    const text = sections[sectionKey]?.result?.text;
    if (!text) return;
    try {
      const res = await axios.post(`${API}/autocue/sessions`, {
        text,
        title: `${SECTION_META[sectionKey].label} — ${agencyName} / ${townName}`,
      });
      setQrForSlug({ slug: res.data.slug, sectionKey });
    } catch {
      toast.error("Could not create autocue session.");
    }
  };

  // Download the section as PDF or DOCX
  const handleDownload = (sectionKey, fmt) => {
    if (!packId) {
      toast.error("Save the pack first by generating it.");
      return;
    }
    const url = `${API}/avatar-onboarding/pack/${packId}/section/${sectionKey}.${fmt}`;
    // The endpoint returns the file with a Content-Disposition header, so opening
    // it in a fresh tab triggers the browser download dialog cleanly
    const a = document.createElement("a");
    a.href = url;
    a.target = "_self";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // History picker — load + show recent packs
  const handleOpenHistory = async () => {
    setShowHistory(true);
    try {
      const res = await axios.get(`${API}/avatar-onboarding/packs?limit=25`);
      setRecentPacks(res.data || []);
    } catch {
      toast.error("Could not load past packs.");
    }
  };

  const handleLoadPack = async (pid) => {
    try {
      const res = await axios.get(`${API}/avatar-onboarding/pack/${pid}`);
      const pack = res.data;
      setPackId(pack.id);
      setAgencyName(pack.agency_name || "");
      setTownName(pack.town_name || "");
      setAgentName(pack.agent_name || "");
      const restored = {};
      SECTION_ORDER.forEach((k) => {
        if (pack.sections?.[k]) {
          restored[k] = { status: "done", result: pack.sections[k] };
        }
      });
      setSections(restored);
      setShowHistory(false);
      toast.success(`Loaded ${pack.agency_name} · ${pack.town_name}`);
    } catch {
      toast.error("Could not load pack.");
    }
  };

  // Open autocue inline in a new tab so the agent can pop it onto a second device
  const handleAutocueOpen = async (sectionKey) => {
    const text = sections[sectionKey]?.result?.text;
    if (!text) return;
    try {
      const res = await axios.post(`${API}/autocue/sessions`, {
        text,
        title: `${SECTION_META[sectionKey].label} — ${agencyName} / ${townName}`,
      });
      window.open(`/autocue/${res.data.slug}`, "_blank", "noopener");
    } catch {
      toast.error("Could not open autocue.");
    }
  };

  const hasAnyResults = useMemo(
    () => SECTION_ORDER.some((k) => sections[k]?.status === "done"),
    [sections]
  );

  return (
    <div className="ao-page" data-testid="avatar-onboarding">
      <AppHeader onBack={onBack} title="Avatar Onboarding" />
      <Toaster position="top-center" richColors closeButton />

      <div className="ao-hero">
        <div className="ao-hero-pill"><Volume2 size={14} /> Voice clone training</div>
        <h1>Generate a complete voice-clone onboarding pack.</h1>
        <p>
          Twenty to thirty minutes of speakable, location-flavoured content tailored to your
          agency and town — ready to read straight off your phone or autocue.
        </p>
      </div>

      <section className="ao-card ao-card-form">
        <div className="ao-card-head">
          <h2>Tell us about the agency</h2>
        </div>
        <div className="ao-form-row">
          <label className="ao-field">
            <span className="ao-label"><Building2 size={13} /> Estate Agency</span>
            <input
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="e.g. Hawkins & Co."
              data-testid="ao-agency"
            />
          </label>
          <label className="ao-field">
            <span className="ao-label"><MapPin size={13} /> Town / Area *</span>
            <input
              type="text"
              value={townName}
              onChange={(e) => setTownName(e.target.value)}
              placeholder="e.g. Wimborne Minster"
              data-testid="ao-town"
            />
          </label>
          <label className="ao-field">
            <span className="ao-label"><User size={13} /> Agent name (optional)</span>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="e.g. Jonathan"
              data-testid="ao-agent"
            />
          </label>
        </div>
        <div className="ao-form-actions">
          <button
            className="ao-primary"
            onClick={handleGeneratePack}
            disabled={isStarting || !agencyName.trim() || !townName.trim()}
            data-testid="ao-generate"
          >
            {isStarting ? (
              <><Loader2 size={16} className="ao-spin" /> Starting…</>
            ) : (
              <><Sparkles size={16} /> Generate onboarding pack</>
            )}
          </button>
          <button
            type="button"
            className="ao-secondary ao-secondary-tall"
            onClick={handleOpenHistory}
            data-testid="ao-history"
            title="Open a past pack"
          >
            <History size={14} /> Past packs
          </button>
          {hasAnyResults && (
            <span className="ao-form-meta">
              Saved for this agency &amp; town — sections below can be regenerated individually.
            </span>
          )}
        </div>
      </section>

      {/* Section cards */}
      <div className="ao-cards-grid">
        {SECTION_ORDER.map((key) => {
          const meta = SECTION_META[key];
          const sec = sections[key] || { status: "idle" };
          return (
            <SectionCard
              key={key}
              meta={meta}
              section={sec}
              onCopy={() => handleCopy(key)}
              onRegen={() => handleRegenerate(key)}
              onAutocue={() => handleAutocueOpen(key)}
              onQr={() => handleQr(key)}
              onDownload={(fmt) => handleDownload(key, fmt)}
              autocueDisabled={key === "interview_qs"}  // Questions aren't run through teleprompter
            />
          );
        })}
      </div>

      {/* Static voice-clone guidance card */}
      <section className="ao-card ao-card-guidance">
        <div className="ao-card-head">
          <span className="ao-eyebrow">Step 5</span>
          <h2><ListChecks size={18} /> Voice clone recording guidance</h2>
        </div>
        <div className="ao-guidance-grid">
          <div>
            <h4>Recording setup</h4>
            <ul>
              <li>Record in a quiet, soft-furnished room (avoids echo)</li>
              <li>Use a decent microphone if you have one — phone is fine in a quiet room</li>
              <li>Sit a consistent distance from the mic (~20 cm / a hand&apos;s span)</li>
              <li>Speak naturally — don&apos;t restart for small slips, just keep going</li>
            </ul>
          </div>
          <div>
            <h4>What to capture</h4>
            <ul>
              <li>Read both generated scripts at a relaxed pace</li>
              <li>Answer all 20 interview questions conversationally</li>
              <li>Aim for 20–30 minutes of total speech</li>
              <li>Vary tone — warm for the area sections, more enthusiastic on the properties</li>
            </ul>
          </div>
        </div>
        <p className="ao-guidance-foot">
          Upload your recording to your account manager once you&apos;re done — we&apos;ll build your AI voice clone from there.
        </p>
      </section>

      {/* Autocue handled by opening in a new tab — leaves room for a future inline modal */}
      {autocueFor && <></>}

      {qrForSlug && (
        <QrModal
          slug={qrForSlug.slug}
          sectionKey={qrForSlug.sectionKey}
          onClose={() => setQrForSlug(null)}
        />
      )}

      {showHistory && (
        <HistoryModal
          packs={recentPacks}
          onLoad={handleLoadPack}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   SectionCard — one carousel-style card per AI-generated step
   ───────────────────────────────────────────────────────────── */
function SectionCard({ meta, section, onCopy, onRegen, onAutocue, onQr, onDownload, autocueDisabled }) {
  const { Icon } = meta;
  const status = section?.status || "idle";

  return (
    <article className={`ao-section ao-section-${meta.accent}`} data-testid={`ao-section-${meta.key}`}>
      <header className="ao-section-head">
        <span className={`ao-section-icon ao-section-icon-${meta.accent}`}>
          <Icon size={18} />
        </span>
        <div className="ao-section-titles">
          <span className="ao-eyebrow">{meta.eyebrow}</span>
          <h3>{meta.label}</h3>
        </div>
        <StatusPill status={status} />
      </header>
      <p className="ao-section-desc">{meta.description}</p>

      {/* States: idle / queued / running / done / error */}
      {status === "idle" && (
        <div className="ao-section-empty">
          Fill in the form above and click <strong>Generate onboarding pack</strong> to get started.
        </div>
      )}
      {(status === "queued" || status === "running") && (
        <div className="ao-section-loading">
          <Loader2 size={20} className="ao-spin" />
          <span>{status === "queued" ? "Queued…" : "Writing this section now…"}</span>
        </div>
      )}
      {status === "error" && (
        <div className="ao-section-error">
          <p>{section.error || "Generation failed."}</p>
          <button className="ao-secondary" onClick={onRegen}><RefreshCw size={14} /> Try again</button>
        </div>
      )}
      {status === "done" && (
        <>
          <div className="ao-meta-row">
            <span><strong>{section.result?.word_count?.toLocaleString() || 0}</strong> words</span>
            <span aria-hidden="true">·</span>
            <span><strong>{section.result?.reading_minutes ?? 0}</strong> min read aloud</span>
          </div>
          <div className="ao-section-body" data-testid={`ao-body-${meta.key}`}>
            {(section.result?.text || "").split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          <div className="ao-section-actions">
            <button className="ao-secondary" onClick={onCopy} data-testid={`ao-copy-${meta.key}`}>
              <Copy size={14} /> Copy
            </button>
            <button className="ao-secondary" onClick={onRegen} data-testid={`ao-regen-${meta.key}`}>
              <RefreshCw size={14} /> Regenerate
            </button>
            <button
              className="ao-secondary"
              onClick={() => onDownload?.("pdf")}
              data-testid={`ao-download-pdf-${meta.key}`}
              title="Download as PDF"
            >
              <Download size={14} /> PDF
            </button>
            <button
              className="ao-secondary"
              onClick={() => onDownload?.("docx")}
              data-testid={`ao-download-docx-${meta.key}`}
              title="Download as Word document"
            >
              <Download size={14} /> Word
            </button>
            {!autocueDisabled && (
              <>
                <button className="ao-secondary" onClick={onAutocue} data-testid={`ao-autocue-${meta.key}`}>
                  <Maximize2 size={14} /> Open in autocue
                </button>
                <button className="ao-secondary" onClick={onQr} data-testid={`ao-qr-${meta.key}`}>
                  <QrCode size={14} /> QR for phone
                </button>
              </>
            )}
          </div>
        </>
      )}
    </article>
  );
}


function StatusPill({ status }) {
  const map = {
    idle:    { label: "Not started", className: "is-idle" },
    queued:  { label: "Queued",      className: "is-running" },
    running: { label: "Generating",  className: "is-running" },
    done:    { label: "Ready",       className: "is-done" },
    error:   { label: "Error",       className: "is-error" },
  };
  const cfg = map[status] || map.idle;
  return <span className={`ao-status-pill ${cfg.className}`}>{cfg.label}</span>;
}


function QrModal({ slug, onClose }) {
  const url = `${window.location.origin}/autocue/${slug}`;
  return (
    <div className="ao-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ao-modal" onClick={(e) => e.stopPropagation()} data-testid="ao-qr-modal">
        <button className="ao-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <h3>Scan to load on your phone</h3>
        <p>
          Open your camera, scan the code, and tap the link to read this script on your phone.
        </p>
        <div className="ao-qr-frame">
          <QRCodeSVG value={url} size={256} bgColor="#ffffff" fgColor="#0d1310" level="M" />
        </div>
        <a className="ao-qr-link" href={url} target="_blank" rel="noopener noreferrer">
          {url} <ChevronRight size={14} />
        </a>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   HistoryModal — list of past packs an account manager can re-open
   ───────────────────────────────────────────────────────────── */
function HistoryModal({ packs, onLoad, onClose }) {
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const fmtMins = (pack) => {
    const total = SECTION_ORDER.reduce(
      (sum, k) => sum + (pack.sections?.[k]?.reading_minutes || 0), 0
    );
    return total ? `${Math.round(total)} min total` : "Incomplete pack";
  };

  return (
    <div className="ao-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="ao-modal ao-modal-wide"
        onClick={(e) => e.stopPropagation()}
        data-testid="ao-history-modal"
      >
        <button className="ao-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <h3>Past voice-clone packs</h3>
        <p>
          Pull up any pack the team has generated. Useful for account managers prepping a recording session — open the pack, hit the autocue button on the section the agent is about to read, and you&apos;re live.
        </p>
        {packs.length === 0 ? (
          <div className="ao-history-empty">No packs yet. Generate one above to get started.</div>
        ) : (
          <ul className="ao-history-list" data-testid="ao-history-list">
            {packs.map((p) => (
              <li key={p.id} className="ao-history-row">
                <div className="ao-history-main">
                  <strong>{p.agency_name}</strong>
                  <span className="ao-history-sub">
                    <MapPin size={11} /> {p.town_name || "—"}
                    {p.agent_name ? <> · <User size={11} /> {p.agent_name}</> : null}
                  </span>
                </div>
                <div className="ao-history-meta">
                  <span><Clock size={11} /> {fmtDate(p.updated_at || p.created_at)}</span>
                  <span>·</span>
                  <span>{fmtMins(p)}</span>
                </div>
                <button
                  className="ao-secondary"
                  onClick={() => onLoad(p.id)}
                  data-testid={`ao-load-${p.id}`}
                >
                  Load <ChevronRight size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

